import {
  useCallback,
  useDeferredValue,
  useMemo,
  useState,
} from "react";
import {
  Ban,
  CheckCircle2,
  Filter,
  KeyRound,
  Plus,
  RefreshCw,
  RotateCw,
  Settings2,
  ShieldCheck,
  UsersRound,
} from "lucide-react";
import {
  createLeadPartner,
  formatDateTime,
  formatRelativeTime,
  listLeadPartners,
  rotateLeadPartnerKey,
  updateLeadPartnerStatus,
  updateLeadPartnerSettings,
} from "@ir/services";
import type {
  LeadPartner,
  LeadPartnerCredentialResponse,
  LeadPartnerStatus,
} from "@ir/types";
import {
  Badge,
  Button,
  ConfirmDialog,
  CopyButton,
  DataTable,
  Field,
  Input,
  Menu,
  Modal,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
  Toolbar,
  useToast,
  type Column,
  type SortState,
} from "@ir/ui";
import { ColumnVisibilityButton } from "../components/ColumnVisibilityButton";
import { useAsyncData } from "../hooks/useAsyncData";
import { useColumnVisibility } from "../hooks/useColumnVisibility";

const PAGE_SIZE = 25;
const SOURCE_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
type PartnerSortBy = "name" | "source" | "status" | "last_used_at" | "expires_at" | "created_at";

function partnerSortByForColumn(columnId: string): PartnerSortBy {
  if (columnId === "partner") return "name";
  if (columnId === "last-used") return "last_used_at";
  if (columnId === "expiry") return "expires_at";
  if (columnId === "created") return "created_at";
  if (["source", "status"].includes(columnId)) return columnId as PartnerSortBy;
  return "created_at";
}

const STATUS_OPTIONS = [
  { value: "", label: "All partners" },
  { value: "active", label: "Active" },
  { value: "disabled", label: "Disabled" },
];

interface PartnerForm {
  name: string;
  source: string;
  rateLimit: string;
  expiresAt: string;
}

const EMPTY_FORM: PartnerForm = {
  name: "",
  source: "",
  rateLimit: "60",
  expiresAt: "",
};

function toLocalDateTimeInput(value: string | Date): string {
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 16);
}

type PendingAction =
  | { kind: "status"; partner: LeadPartner; status: LeadPartnerStatus }
  | { kind: "rotate"; partner: LeadPartner };

export function LeadPartnersPage() {
  const toast = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const deferredSearch = useDeferredValue(searchTerm);
  const [status, setStatus] = useState<LeadPartnerStatus | "">("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState>({ columnId: "created", direction: "desc" });
  const [refreshToken, setRefreshToken] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<LeadPartner | null>(null);
  const [form, setForm] = useState<PartnerForm>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [credential, setCredential] = useState<LeadPartnerCredentialResponse | null>(null);

  const refresh = useCallback(() => setRefreshToken((value) => value + 1), []);
  const result = useAsyncData(
    (signal) =>
      listLeadPartners({
        searchTerm: deferredSearch,
        status,
        pageNumber: page,
        pageSize: PAGE_SIZE,
        sortBy: partnerSortByForColumn(sort.columnId),
        sortDirection: sort.direction,
        signal,
      }),
    [deferredSearch, status, page, sort, refreshToken],
  );

  const openCreate = () => {
    setEditingPartner(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setCreateOpen(true);
  };

  const openEdit = (partner: LeadPartner) => {
    setEditingPartner(partner);
    setForm({
      name: partner.name,
      source: partner.source,
      rateLimit: String(partner.rate_limit_per_minute),
      expiresAt: partner.expires_at ? toLocalDateTimeInput(partner.expires_at) : "",
    });
    setFormError(null);
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    const name = form.name.trim();
    const source = form.source.trim().toLowerCase();
    const rateLimit = Number(form.rateLimit);
    if (!name) return setFormError("Partner name is required.");
    if (!SOURCE_PATTERN.test(source)) {
      return setFormError("Source must use lowercase letters, numbers and hyphens.");
    }
    if (!Number.isInteger(rateLimit) || rateLimit < 1 || rateLimit > 1000) {
      return setFormError("Rate limit must be a whole number from 1 to 1000.");
    }

    setSaving(true);
    setFormError(null);
    try {
      const expiresAt = form.expiresAt ? new Date(form.expiresAt).toISOString() : null;
      if (editingPartner) {
        const updated = await updateLeadPartnerSettings(editingPartner.id, {
          name,
          rate_limit_per_minute: rateLimit,
          expires_at: expiresAt,
        });
        setCreateOpen(false);
        setEditingPartner(null);
        refresh();
        toast.success("Partner settings updated", `${updated.name}'s settings are now active.`);
        return;
      }

      const created = await createLeadPartner({
        name,
        source,
        rate_limit_per_minute: rateLimit,
        expires_at: expiresAt,
      });
      setCreateOpen(false);
      setCredential(created);
      refresh();
      toast.success("Lead partner created", `${created.partner.name} can now submit leads.`);
    } catch (error) {
      setFormError(error instanceof Error ? error.message : "Could not create that partner.");
    } finally {
      setSaving(false);
    }
  };

  const confirmAction = async () => {
    if (!pendingAction) return;
    setSaving(true);
    try {
      if (pendingAction.kind === "rotate") {
        const rotated = await rotateLeadPartnerKey(pendingAction.partner.id);
        setCredential(rotated);
        toast.success("API key rotated", "The previous key stopped working immediately.");
      } else {
        await updateLeadPartnerStatus(pendingAction.partner.id, pendingAction.status);
        toast.success(
          pendingAction.status === "active" ? "Partner enabled" : "Partner disabled",
          pendingAction.status === "active"
            ? `${pendingAction.partner.name} can submit leads again.`
            : `${pendingAction.partner.name} can no longer submit leads.`,
        );
      }
      setPendingAction(null);
      refresh();
    } catch (error) {
      toast.error("Could not update that partner", error instanceof Error ? error.message : undefined);
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo<Column<LeadPartner>[]>(
    () => [
      {
        id: "partner",
        header: "Lead partner",
        sortable: true,
        cell: (partner) => (
          <div className="min-w-0">
            <p className="truncate font-medium text-content">{partner.name}</p>
            <p className="truncate font-mono text-xs text-content-subtle">{partner.source}</p>
          </div>
        ),
      },
      {
        id: "status",
        header: "Status",
        width: "8rem",
        sortable: true,
        cell: (partner) => (
          <Badge tone={partner.status === "active" ? "success" : "neutral"} dot>
            {partner.status === "active" ? "Active" : "Disabled"}
          </Badge>
        ),
      },
      {
        id: "credential",
        header: "Credential",
        hideBelow: "md",
        cell: (partner) => (
          <div>
            <code className="text-xs text-content-muted">{partner.key_prefix}</code>
            <p className="mt-0.5 text-xs text-content-subtle">Version {partner.key_version}</p>
          </div>
        ),
      },
      {
        id: "limit",
        header: "Limit",
        hideBelow: "lg",
        cell: (partner) => (
          <span className="whitespace-nowrap text-sm text-content-muted">
            {partner.rate_limit_per_minute}/minute
          </span>
        ),
      },
      {
        id: "last-used",
        header: "Last used",
        hideBelow: "lg",
        sortable: true,
        cell: (partner) =>
          partner.last_used_at ? (
            <span
              className="whitespace-nowrap text-content-muted"
              title={formatDateTime(partner.last_used_at)}
            >
              {formatRelativeTime(partner.last_used_at)}
            </span>
          ) : (
            <span className="text-content-subtle">Never</span>
          ),
      },
      {
        id: "expiry",
        header: "Expires",
        hideBelow: "xl",
        sortable: true,
        cell: (partner) =>
          partner.expires_at ? (
            <span
              className="whitespace-nowrap text-content-muted"
              title={formatDateTime(partner.expires_at)}
            >
              {formatRelativeTime(partner.expires_at)}
            </span>
          ) : (
            <span className="text-content-subtle">No expiry</span>
          ),
      },
      {
        id: "actions",
        header: "",
        mobileLabel: "Actions",
        align: "right",
        width: "4rem",
        cell: (partner) => (
          <Menu
            label={`Actions for ${partner.name}`}
            items={[
              {
                id: "settings",
                label: "Edit settings",
                icon: Settings2,
                onSelect: () => openEdit(partner),
              },
              {
                id: "rotate",
                label: "Rotate API key",
                icon: RotateCw,
                onSelect: () => setPendingAction({ kind: "rotate", partner }),
              },
              partner.status === "active"
                ? {
                    id: "disable",
                    label: "Disable submissions",
                    icon: Ban,
                    danger: true,
                    separated: true,
                    onSelect: () =>
                      setPendingAction({ kind: "status", partner, status: "disabled" }),
                  }
                : {
                    id: "enable",
                    label: "Enable submissions",
                    icon: CheckCircle2,
                    separated: true,
                    onSelect: () =>
                      setPendingAction({ kind: "status", partner, status: "active" }),
                  },
            ]}
            trigger={({ open, toggle }) => (
              <button
                type="button"
                onClick={toggle}
                aria-expanded={open}
                aria-label={`Actions for ${partner.name}`}
                className="grid size-8 place-items-center rounded-md text-content-subtle transition-colors hover:bg-surface-sunken hover:text-content"
              >
                ⋯
              </button>
            )}
          />
        ),
      },
    ],
    [],
  );

  const columnVisibility = useColumnVisibility(
    "lead-partners",
    columns,
    ["partner", "status", "credential", "limit", "last-used", "expiry"],
  );

  const updateSort = (next: SortState) => {
    setSort(next);
    setPage(1);
  };

  const actionIsRotate = pendingAction?.kind === "rotate";

  return (
    <>
      <PageHeader
        icon={UsersRound}
        title="Lead partners"
        description="Issue and control credentials for approved third parties that submit fast-food leads."
        actions={
          <>
            <Button variant="secondary" icon={RefreshCw} loading={result.refreshing} onClick={refresh}>
              Refresh
            </Button>
            <Button icon={Plus} onClick={openCreate}>Add partner</Button>
          </>
        }
      >
        <Toolbar>
          <SearchInput
            className="flex-1"
            value={searchTerm}
            onChange={(value) => {
              setSearchTerm(value);
              setPage(1);
            }}
            placeholder="Search by partner name or source"
            ariaLabel="Search lead partners"
          />
          <Select
            className="sm:w-44"
            icon={Filter}
            options={STATUS_OPTIONS}
            value={status}
            aria-label="Filter by partner status"
            onChange={(event) => {
              setStatus(event.target.value as LeadPartnerStatus | "");
              setPage(1);
            }}
          />
          <ColumnVisibilityButton
            columns={columnVisibility.selectableColumns}
            visibleColumnIds={columnVisibility.visibleColumnIds}
            minimumReached={columnVisibility.minimumReached}
            onToggle={columnVisibility.toggleColumn}
            onReset={columnVisibility.resetColumns}
          />
        </Toolbar>
      </PageHeader>

      <div className="mb-5 flex gap-3 rounded-xl border border-gold-300/60 bg-gold-50 p-4 text-sm text-gold-900 dark:border-gold-800 dark:bg-gold-950/40 dark:text-gold-200">
        <ShieldCheck className="mt-0.5 shrink-0" size={18} aria-hidden="true" />
        <p>API keys are shown once. The marketplace stores only a salted hash, and rotation invalidates the previous key immediately.</p>
      </div>

      <DataTable
        caption="Lead partners"
        columns={columnVisibility.visibleColumns}
        rows={result.data?.items ?? []}
        rowKey={(partner) => partner.id}
        loading={result.loading}
        refreshing={result.refreshing}
        error={result.error}
        onRetry={result.reload}
        sort={sort}
        onSortChange={updateSort}
        emptyIcon={KeyRound}
        emptyTitle="No lead partners yet"
        emptyDescription="Create a partner when an approved third party needs to submit fast-food leads."
        emptyAction={<Button icon={Plus} onClick={openCreate}>Add partner</Button>}
      />

      {result.data && (
        <Pagination
          currentPage={result.data.pageNumber}
          totalPages={result.data.totalPages}
          totalCount={result.data.totalCount}
          pageSize={result.data.pageSize}
          disabled={result.refreshing}
          onPageChange={setPage}
        />
      )}

      <Modal
        open={createOpen}
        onClose={() => {
          if (!saving) {
            setCreateOpen(false);
            setEditingPartner(null);
          }
        }}
        title={editingPartner ? "Edit lead partner" : "Add lead partner"}
        description={
          editingPartner
            ? "Source attribution is immutable, but operational limits can be updated safely."
            : "The source identifier is written to every quote submitted with this credential."
        }
        dismissOnOverlayClick={!saving}
        footer={
          <>
            <Button
              variant="secondary"
              disabled={saving}
              onClick={() => {
                setCreateOpen(false);
                setEditingPartner(null);
              }}
            >
              Cancel
            </Button>
            <Button
              icon={editingPartner ? Settings2 : KeyRound}
              loading={saving}
              onClick={() => void submitCreate()}
            >
              {editingPartner ? "Save settings" : "Create and generate key"}
            </Button>
          </>
        }
      >
        <form
          className="grid gap-4 pt-1 sm:grid-cols-2"
          onSubmit={(event) => {
            event.preventDefault();
            void submitCreate();
          }}
        >
          <Field label="Partner name" required className="sm:col-span-2">
            <Input
              value={form.name}
              onChange={(event) => setForm((value) => ({ ...value, name: event.target.value }))}
              placeholder="Example Lead Supplier"
            />
          </Field>
          <Field
            label="Source identifier"
            required
            hint="Lowercase letters, numbers and hyphens only. This cannot be reused."
            className="sm:col-span-2"
          >
            <Input
              value={form.source}
              disabled={editingPartner !== null}
              onChange={(event) =>
                setForm((value) => ({
                  ...value,
                  source: event.target.value.toLowerCase().replace(/\s+/g, "-"),
                }))
              }
              placeholder="example-leads"
            />
          </Field>
          <Field label="Requests per minute" required hint="Allowed range: 1 to 1000.">
            <Input
              type="number"
              min={1}
              max={1000}
              value={form.rateLimit}
              onChange={(event) => setForm((value) => ({ ...value, rateLimit: event.target.value }))}
            />
          </Field>
          <Field label="Credential expiry" hint="Optional. Leave empty for no expiry.">
            <Input
              type="datetime-local"
              min={toLocalDateTimeInput(new Date())}
              value={form.expiresAt}
              onChange={(event) => setForm((value) => ({ ...value, expiresAt: event.target.value }))}
            />
          </Field>
          {formError && (
            <p role="alert" className="rounded-lg border border-danger-500/30 bg-danger-50 p-3 text-sm text-danger-700 dark:bg-danger-500/10 dark:text-danger-400 sm:col-span-2">
              {formError}
            </p>
          )}
          <button type="submit" className="sr-only">Create partner</button>
        </form>
      </Modal>

      <Modal
        open={credential !== null}
        onClose={() => setCredential(null)}
        title="Store this API key now"
        description="For security, this full credential cannot be retrieved again after you close this window."
        dismissOnOverlayClick={false}
        footer={<Button onClick={() => setCredential(null)}>I have stored the key</Button>}
      >
        {credential && (
          <div className="pt-2">
            <p className="text-sm text-content-muted">
              Credential for <span className="font-medium text-content">{credential.partner.name}</span>
            </p>
            <div className="mt-3 flex items-start gap-2 rounded-xl border border-line-strong bg-surface-sunken p-3">
              <code className="min-w-0 flex-1 select-all break-all font-mono text-sm text-content">
                {credential.api_key}
              </code>
              <CopyButton value={credential.api_key} label="Copy API key" />
            </div>
            <p className="mt-3 text-xs text-content-subtle">
              Send it in the <code className="font-mono">x-api-key</code> header. Every submission also requires a unique <code className="font-mono">Idempotency-Key</code> header.
            </p>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={pendingAction !== null}
        onClose={() => !saving && setPendingAction(null)}
        onConfirm={confirmAction}
        loading={saving}
        destructive={actionIsRotate || (pendingAction?.kind === "status" && pendingAction.status === "disabled")}
        title={
          actionIsRotate
            ? "Rotate this API key?"
            : pendingAction?.kind === "status" && pendingAction.status === "disabled"
              ? "Disable this lead partner?"
              : "Enable this lead partner?"
        }
        confirmLabel={actionIsRotate ? "Rotate key" : pendingAction?.kind === "status" && pendingAction.status === "disabled" ? "Disable partner" : "Enable partner"}
        description={
          actionIsRotate
            ? "The current key will stop working immediately. A replacement will be shown once."
            : pendingAction?.kind === "status" && pendingAction.status === "disabled"
              ? "New submissions using this partner's key will be rejected immediately."
              : "This partner will be allowed to submit fast-food leads again."
        }
      />
    </>
  );
}
